/**
 * Layout 2a: coluna de conteúdo + rail de âncoras (design-references/
 * admin-hub/round-1/"02 Dominio IA e conduta (pagina padrao).dc.html", opção
 * 2a — recomendada). Composto pelas telas de domínio (T7-T11): elas passam as
 * `DomainSection` reais como children; este componente só monta o cabeçalho,
 * o rail de "Nesta página" e a aterrissagem por âncora.
 *
 * Read-only por construção: nenhuma escrita mora aqui (1 caminho de escrita
 * por dado) — o form real vive dentro de cada `DomainSection`.
 *
 * Desvio DECLARADO de fidelidade (fix round 1 — ruling do coordenador mantém
 * a escolha original, revisitando o Concern 1 do relatório da T4): a prancha
 * 02 renderiza a 2a ISOLADA, com o rail de âncoras (230px) colado direto no
 * conteúdo. Mas a 4a (prancha 04 — a variante que o TL escolheu para o shell,
 * já construída em `AdminShell.tsx`) exige um rail global (232px) + o rail
 * próprio do shell (210px) ANTES de qualquer conteúdo de domínio. Empilhar as
 * duas leituras literalmente puxaria 232+210+230=672px de cromo antes do
 * conteúdo — o que nenhuma prancha desenha. Colocar o rail de âncoras à
 * DIREITA (como o código desta task sempre teve) resolve a colisão e segue a
 * convenção comum (esquerda = onde você está no produto; direita = onde você
 * está na página), preservando a substância da 2a (âncoras + estado por
 * seção + deep-link). Fica registrado para o TL reverter se preferir a
 * leitura literal da prancha 02 — é uma troca de ordem no flex.
 *
 * D5 (30/Set/2026, prancha 00-B/2a): o rail do shell morreu — os domínios vivem no
 * menu principal. O índice fica À DIREITA por decisão, não por colisão: é posição
 * dentro da página, não navegação. A página ganha, no FIM, o bloco «Ferramentas deste
 * domínio» (`FerramentasDoDominio`) e o índice a linha «Ferramentas · N». O parágrafo
 * acima sobre 232+210+230px é histórico da colisão que deixou de existir.
 *
 * Finding 1 (fix round 1): o rail agora consome `useConfigEstados()` (T2) —
 * até este fix round o resolver não tinha NENHUM consumidor no hub. Cada
 * item do rail ganha um indicador de estado (bolinha ligada/desligada, traço
 * honesto quando indisponível, badge de tier quando locked) — a metade da
 * anotação da prancha 02 ("estado por seção no rail") que faltava.
 *
 * Finding 2 (fix round 1): breadcrumb "Administração › <domínio>" acima do
 * h1 — com o rail de âncoras à direita, é o único elemento dizendo em qual
 * domínio do ambiente de Administração a pessoa está.
 *
 * Finding 1 (fix round 2 — achado do coordenador, segurança): o rail listava
 * TODO item do catálogo, inclusive os `omitWhenBarred` sem a capability —
 * `DomainSection` corretamente omite o corpo (`fila-pos-transferencia`
 * some), mas o rail continuava desenhando `<a href="#fila-pos-transferencia">`
 * — um link morto que denuncia a existência do recurso a um papel que nem
 * deveria saber que ele existe (o oposto do propósito de `omitWhenBarred`,
 * nav-model.ts:9-17). Corrigido filtrando `itens` ANTES do render, usando
 * `useCapabilities()` — a mesma fonte (store de auth) que `DomainSection`
 * consulta via `useCapability(item.capability)` — para que rail e corpo
 * NUNCA possam discordar sobre o mesmo item.
 *
 * Finding 2 (fix round 2, minor): item `locked` sem `tierLabel` (ex.:
 * `conduta`, `confirmacao-cadastral` — gateados por `tenant-config-read`)
 * ficava sem NENHUM indicador no rail, enquanto o corpo da seção mostra a
 * explicação genérica "plano superior". Rail e corpo devem contar a mesma
 * história — adicionado um ícone de cadeado neutro (`lucide-react`, sem
 * emoji) para esse caso.
 */
import { Lock } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { useCapabilities } from '../../../hooks/use-capability';

import { DOMINIOS, secoesByDominio } from './catalog';
import {
  FERRAMENTAS_ANCHOR,
  FerramentasDoDominio,
  ferramentasVisiveis,
} from './FerramentasDoDominio';
import './hub.css';
import { useAnchorLanding } from './use-anchor-landing';
import { useConfigEstados } from './use-config-estados';

import type { ConfigCatalogItem, DominioId } from './catalog';
import type { EstadoConfig } from './estado-resolver';

/**
 * Indicador de estado do item no rail (Finding 1, fix round 1). Vocabulário
 * reaproveitado: `EstadoConfig` (estado-resolver.ts) para o dado,
 * `text-accent`/`bg-accent/10` (mesmo par de classes do badge de tier em
 * `DomainSection.tsx`) para o tier. `indisponivel` e "sem resolver" (estado
 * ausente do Map) NÃO desenham bolinha — o hub não chuta um estado que não
 * tem fonte carregada. `locked` sem `tierLabel` ganha um cadeado neutro
 * (Finding 2, fix round 2) — nunca fica sem indicador nenhum.
 */
function RailIndicator({
  estado,
  item,
}: {
  readonly estado: EstadoConfig | null | undefined;
  readonly item: ConfigCatalogItem;
}): JSX.Element | null {
  if (estado === 'locked') {
    if (item.tierLabel !== undefined) {
      return (
        <span className="ml-auto rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent">
          {item.tierLabel}
        </span>
      );
    }
    return (
      <span role="img" aria-label="Bloqueado pelo plano atual" className="ml-auto flex-shrink-0">
        <Lock aria-hidden className="h-3 w-3 text-text-secondary" />
      </span>
    );
  }
  if (estado === 'soon') {
    return (
      <span
        aria-hidden
        className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full border border-dashed border-border-subtle"
      />
    );
  }
  if (estado === 'ligado') {
    return (
      <span aria-hidden className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-500" />
    );
  }
  if (estado === 'desligado' || estado === 'nao-configurado') {
    return (
      <span
        aria-hidden
        className="ml-auto h-1.5 w-1.5 flex-shrink-0 rounded-full border border-neutral-400"
      />
    );
  }
  if (estado === 'indisponivel') {
    return (
      <span className="ml-auto text-xs text-text-secondary" aria-label="Estado indisponível">
        —
      </span>
    );
  }
  // `estado` null/undefined: item sem resolver — nenhum badge (mesma regra de estado-resolver.ts).
  return null;
}

export function DomainPage({
  dominio,
  children,
}: {
  readonly dominio: DominioId;
  readonly children: React.ReactNode;
}): JSX.Element {
  const def = DOMINIOS.find((d) => d.id === dominio)!;
  const { hash } = useLocation();
  useAnchorLanding(hash);
  const estados = useConfigEstados();
  const capabilities = useCapabilities();
  // Fonte única com `DomainSection` (mesmo store de auth) — rail e corpo nunca
  // podem discordar sobre um item `omitWhenBarred` sem a capability.
  const itens = secoesByDominio(dominio).filter(
    (i) => i.omitWhenBarred !== true || capabilities.includes(i.capability),
  );
  // D5: as ferramentas do domínio (bloco no fim + linha no índice) — mesma peneira do bloco.
  const nFerramentas = ferramentasVisiveis(dominio, capabilities).length;
  return (
    <div className="flex h-full">
      <div className="max-w-3xl flex-1 px-8 py-6">
        <header className="border-b border-border-subtle pb-4">
          <p className="flex items-center gap-1.5 text-xs text-text-secondary">
            <span>Administração</span>
            <span aria-hidden>/</span>
            <strong className="font-medium text-text-primary">{def.label}</strong>
          </p>
          <h1 className="mt-1 text-xl font-semibold text-text-primary">{def.label}</h1>
          <p className="mt-0.5 text-sm text-text-secondary">{def.descricao}</p>
        </header>
        {children}
        <FerramentasDoDominio dominio={dominio} />
      </div>
      <nav
        aria-label={`Seções de ${def.label}`}
        className="sticky top-0 hidden w-56 flex-shrink-0 self-start px-4 py-6 xl:block"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
          Nesta página
        </p>
        <ul className="space-y-1 border-l border-border-subtle">
          {itens.map((i) => (
            <li key={i.id}>
              <a
                href={`#${i.id}`}
                className={`flex items-center gap-2 px-3 py-1 text-sm ${
                  hash === `#${i.id}`
                    ? '-ml-px border-l-2 border-accent font-medium text-text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <span>{i.label}</span>
                <RailIndicator estado={estados.get(i.id)} item={i} />
              </a>
            </li>
          ))}
        </ul>
        {nFerramentas > 0 && (
          <ul className="mt-2 border-l border-border-subtle">
            <li>
              <a
                href={`#${FERRAMENTAS_ANCHOR}`}
                className={`flex items-center gap-2 px-3 py-1 text-sm ${
                  hash === `#${FERRAMENTAS_ANCHOR}`
                    ? '-ml-px border-l-2 border-accent font-medium text-text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <span>Ferramentas</span>
                <span className="ml-auto font-mono text-[10px] text-text-secondary">
                  {nFerramentas}
                </span>
              </a>
            </li>
          </ul>
        )}
      </nav>
    </div>
  );
}
